const DISMISSED_KEY = 'side-action-bar:dismissed-at';
const SUBSCRIBED_KEY = 'side-action-bar:subscribed';

function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // Storage can be unavailable (private mode); the popup then simply may show again.
  }
}

class SideActionBar extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.updateChat = this.updateChat.bind(this);
    this.onInterestChange = this.onInterestChange.bind(this);
    this.scrollTimer = null;
    this.autoOpenTimer = null;
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.overlay = this.querySelector('.side-action-bar__overlay');
    this.topButton = this.querySelector('[data-side-action="top"]');
    this.chatButton = this.querySelector('[data-side-action="chat"]');
    this.addEventListener('click', this.onClick);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('load', this.updateChat);
    if (this.dialog) {
      this.dialog.addEventListener('close', this.onDialogClose);
    }
    this.addEventListener('change', this.onInterestChange);
    this.updateTopButton();
    this.updateChat();

    // After the signup form posts, the page reloads: reopen the popup to show the result.
    const posted = this.querySelector('[data-side-action-bar-posted]');
    if (posted) {
      if (posted.dataset.sideActionBarPosted === 'success') writeStorage(SUBSCRIBED_KEY, '1');
      this.openPanel();
    } else {
      this.scheduleAutoOpen();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('load', this.updateChat);
    this.removeEventListener('change', this.onInterestChange);
    clearTimeout(this.scrollTimer);
    clearTimeout(this.autoOpenTimer);
    if (this.dialog) {
      this.dialog.removeEventListener('close', this.onDialogClose);
    }
    document.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('side-action-bar--open');
  }

  onClick(event) {
    const control = event.target.closest('[data-side-action]');
    if (!control) return;
    const action = control.dataset.sideAction;
    if (action === 'promo') return this.openPanel();
    if (action === 'close') return this.closePanel();
    if (action === 'chat') return this.toggleChat();
    if (action === 'top') return this.scrollToTop();
  }

  // Slide the bar out of the way while scrolling and bring it back once scrolling stops.
  onScroll() {
    this.updateTopButton();
    this.classList.add('side-action-bar--scrolling');
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => this.classList.remove('side-action-bar--scrolling'), 300);
  }

  updateTopButton() {
    if (this.topButton) this.topButton.classList.toggle('is-visible', window.scrollY > 100);
  }

  // The chat button only makes sense when the Shopify Inbox chat embed is on the page.
  updateChat() {
    if (this.chatButton) this.chatButton.hidden = !document.querySelector('shopify-chat');
  }

  toggleChat() {
    const chat = document.querySelector('shopify-chat');
    if (!chat) return;
    if (chat.open) chat.close();
    else chat.show();
  }

  scrollToTop() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reducedMotion ? 'instant' : 'smooth' });
  }

  // Open the popup once after a delay, unless it was closed recently or the visitor already subscribed.
  scheduleAutoOpen() {
    const delay = Number(this.dataset.autoOpenDelay);
    if (!this.dialog || !delay) return;
    if (readStorage(SUBSCRIBED_KEY)) return;
    const days = Number(this.dataset.autoOpenDays) || 7;
    const dismissedAt = Number(readStorage(DISMISSED_KEY));
    if (dismissedAt && Date.now() - dismissedAt < days * 86400000) return;
    this.autoOpenTimer = setTimeout(() => {
      // Do not interrupt another open dialog (for example the cart or a quick view).
      if (!this.dialog.open && !document.querySelector('dialog[open]')) this.openPanel();
    }, delay * 1000);
  }

  // Keep the customer tags field in sync with the selected interest options.
  onInterestChange(event) {
    if (!event.target.matches('[data-side-action-bar-interest]')) return;
    const form = event.target.closest('form');
    const tags = form.querySelector('[data-side-action-bar-tags]');
    const selected = [...form.querySelectorAll('[data-side-action-bar-interest]:checked')].map((input) => input.value);
    tags.value = [tags.dataset.baseTag, ...selected].join(',');
  }

  // A non-modal dialog (not showModal) keeps the popup out of the top layer, so the hCaptcha
  // challenge that Shopify adds to the body on submit can render above it.
  openPanel() {
    if (!this.dialog || this.dialog.open) return;
    this.lastTrigger = document.activeElement;
    this.overlay.hidden = false;
    document.body.classList.add('side-action-bar--open');
    document.addEventListener('keydown', this.onKeydown);
    this.dialog.show();
  }

  onKeydown(event) {
    if (!this.dialog.open) return;
    if (event.key === 'Escape') return this.closePanel();
    if (event.key !== 'Tab') return;
    const focusable = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled])')];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  closePanel() {
    if (this.dialog.open) this.dialog.close();
    this.onDialogClose();
  }

  // Idempotent clean-up, called directly because a non-modal dialog does not reliably fire "close".
  onDialogClose() {
    this.overlay.hidden = true;
    document.body.classList.remove('side-action-bar--open');
    document.removeEventListener('keydown', this.onKeydown);
    if (!this.lastTrigger) return;
    writeStorage(DISMISSED_KEY, String(Date.now()));
    if (this.lastTrigger.isConnected) this.lastTrigger.focus();
    this.lastTrigger = null;
  }
}

if (!customElements.get('side-action-bar')) {
  customElements.define('side-action-bar', SideActionBar);
}
